import { trpc } from '../utils/trpc';
import styles from '../styles/EventStatsModal.module.css';

// ─── Helpers ──────────────────────────────────────────────────

function attendanceRate(attended: number, total: number): number {
  if (!total) return 0;
  return Math.round((attended / total) * 100);
}

function LikeDislikeBar({ likes, dislikes }: { likes: number; dislikes: number }) {
  const total = likes + dislikes;
  const likePercent = total > 0 ? Math.round((likes / total) * 100) : 0;

  return (
    <div className={styles.ratingBar}>
      <div className={styles.ratingCounts}>
        <span className={styles.likeCount}>👍 {likes}</span>
        <span className={styles.dislikeCount}>👎 {dislikes}</span>
      </div>
      {total > 0 && (
        <div className={styles.ratingTrack}>
          <div
            className={styles.ratingFill}
            style={{ width: `${likePercent}%` }}
            aria-label={`${likePercent}% positive`}
          />
        </div>
      )}
      {total > 0 && <span className={styles.ratingPercent}>{likePercent}% positive</span>}
      {total === 0 && <span className={styles.tileSub}>No ratings yet</span>}
    </div>
  );
}

// ─── Stat tile ────────────────────────────────────────────────

function StatTile({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string | number;
  sub?: string;
  accent?: 'gold' | 'green' | 'orange';
}) {
  return (
    <div className={`${styles.tile} ${accent ? styles[`tile_${accent}`] : ''}`}>
      <span className={styles.tileLabel}>{label}</span>
      <span className={styles.tileValue}>{value}</span>
      {sub && <span className={styles.tileSub}>{sub}</span>}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────

interface EventStatsModalProps {
  eventId: string;
  eventName: string;
  onClose: () => void;
}

export default function EventStatsModal({
  eventId,
  eventName,
  onClose,
}: EventStatsModalProps) {
  // ── Queries ──
  const { data: event } = trpc.event.getEventById.useQuery({ eventId });
  const { data: goingStats } = trpc.event.getGoingCount.useQuery({ eventId });
  const { data: attendedStats } = trpc.event.getAttendedCount.useQuery({ eventId });
  const { data: ratingStats } = trpc.event.getRatingStats.useQuery({ eventId });
  const { data: visitorTags } = trpc.event.getVisitorTags.useQuery({ event_id: eventId });

  // ── Derived values ──
  const goingCount = goingStats?.count ?? 0;
  const attendedCount = attendedStats?.count ?? 0;
  const totalCount = goingCount + attendedCount;
  const rate = attendanceRate(attendedCount, totalCount);

  const likes = ratingStats?.likes ?? 0;
  const dislikes = ratingStats?.dislikes ?? 0;

  const commonTags: string[] =
    event?.event_tags
      ?.map((et: { tags: { name: string } | null }) => et.tags?.name ?? '')
      .filter(Boolean) ?? [];

  // Top 5 visitor interest tags by frequency
  const topVisitorTags = visitorTags?.slice(0, 5) ?? [];

  function handleBackdrop(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className={styles.backdrop}
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
    >
      <div className={styles.modal}>
        {/* ── Header ── */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerEyebrow}>Event Statistics</span>
            <h2 className={styles.headerTitle}>{eventName}</h2>
          </div>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close statistics"
          >
            ✕
          </button>
        </div>

        {/* ── Shimmer stripe ── */}
        <div className={styles.stripe}>
          <div className="gsShimmerBar" />
        </div>

        {/* ── KPI grid ── */}
        <div className={styles.body}>
          <div className={styles.tileGrid}>
            <StatTile
              label="RSVP"
              value={totalCount}
              sub="rsvped as going"
              accent="green"
            />
            <StatTile
              label="Attendance Rate"
              value={totalCount > 0 ? `${rate}%` : '—'}
              sub="attended vs going + attended"
              accent={
                totalCount === 0
                  ? undefined
                  : rate >= 80
                    ? 'green'
                    : rate >= 50
                      ? 'gold'
                      : 'orange'
              }
            />
            <StatTile
              label="Attended"
              value={attendedCount}
              sub="confirmed showed up"
              accent="gold"
            />
            <StatTile
              label="Revenue"
              value="—"
              sub="ticketing not yet available"
              accent={undefined}
            />
          </div>

          {/* ── Event Tags ── */}
          <div className={styles.section}>
            <span className={styles.sectionLabel}>Event Interests</span>
            <div className={styles.tagList}>
              {commonTags.length > 0 ? (
                commonTags.map((tag) => (
                  <span key={tag} className={styles.tag}>
                    {tag}
                  </span>
                ))
              ) : (
                <span className={styles.tileSub}>No interests</span>
              )}
            </div>
          </div>

          {/* ── Visitor Interest Tags ── */}
          <div className={styles.section}>
            <span className={styles.sectionLabel}>Attendee Interests</span>
            <div className={styles.tagList}>
              {topVisitorTags.length > 0 ? (
                topVisitorTags.map((t) => (
                  <span key={t.id} className={styles.tag}>
                    {t.name}
                    <span className={styles.tagCount}> ×{t.count}</span>
                  </span>
                ))
              ) : (
                <span className={styles.tileSub}>No attendee data yet</span>
              )}
            </div>
          </div>

          {/* ── Attendee Ratings ── */}
          <div className={styles.section}>
            <span className={styles.sectionLabel}>Attendee Rating</span>
            <div className={styles.satisfactionCard}>
              <LikeDislikeBar likes={likes} dislikes={dislikes} />
              <p className={styles.satisfactionNote}>
                Ratings submitted by attendees after the event concluded.
              </p>
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className={styles.footer}>
          <button className={styles.btnClose} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
